/**
 * SuggestionListItem - List row for a suggestion or shortcut in the expanded peek sheet.
 *
 * Layout: [category icon] [label + description]
 * Clicking fires the chip action (opens sidebar with query).
 * No save/edit/remove actions (unlike ShortcutBrowseItem).
 */

import {
  categoryConfig,
  getCategoryIcon,
  type SuggestedChip,
} from '@/components/ui/transcript-chips/types';
import { cn } from '@/lib/cn';
import {
  TypographyCaption,
  TypographyFootNoteBold,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { SlashSquare } from 'lucide-react';
import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { protocolsListViewMessages } from './protocols-list-view.messages';

interface SuggestionListItemProps {
  chip: SuggestedChip & { isDefault?: boolean; teamShortcutId?: string };
  onClick?: (chip: SuggestedChip) => void;
}

export const SuggestionListItem = memo(({ chip, onClick }: SuggestionListItemProps) => {
  const isPinned = chip.isPermanent ?? false;
  const config = categoryConfig[chip.category];
  const isDestructive = config?.isDestructive ?? false;

  const iconColorClass = isDestructive
    ? 'text-validation-warning'
    : isPinned
      ? 'text-tier-pro-text'
      : 'text-text-secondary';

  const iconBgClass = isDestructive
    ? 'bg-validation-warning-muted'
    : isPinned
      ? 'bg-tier-pro-muted'
      : 'bg-surface-accent';

  const Icon = isPinned ? SlashSquare : getCategoryIcon(chip.category);

  return (
    <button
      type="button"
      onClick={() => onClick?.(chip)}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left',
        'transition-colors hover:bg-surface-hover',
        'cursor-pointer'
      )}
      data-testid={`suggestion-list-item-${chip.id}`}
    >
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg',
          iconBgClass
        )}
      >
        <Icon size={16} className={iconColorClass} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <TypographyP2Bold className="truncate">{chip.label}</TypographyP2Bold>
          {chip.teamShortcutId && (
            <TypographyFootNoteBold className="inline-flex shrink-0 items-center rounded-full bg-surface-accent px-1.5 py-0.5 uppercase tracking-wide text-text-secondary">
              <FormattedMessage {...protocolsListViewMessages.teamBadge} />
            </TypographyFootNoteBold>
          )}
        </div>
        <TypographyCaption className="line-clamp-1 text-text-secondary">
          {chip.query}
        </TypographyCaption>
      </div>
    </button>
  );
});

SuggestionListItem.displayName = 'SuggestionListItem';
